import React, { useState, useRef } from 'react';
import {
  Box, Typography, Popover, List, ListItemButton, ListItemText,
  TextField, Button, Divider
} from '@mui/material';
import {
  CalendarMonth as CalendarIcon,
  ArrowDropDown as ArrowDropDownIcon
} from '@mui/icons-material';
import { useTheme } from '@mui/material/styles';
import dayjs from 'dayjs';
import { useGoogleAdsDate, DATE_PRESETS } from '../../contexts/GoogleAdsDateContext';
import { useTranslation } from 'react-i18next';
import { getLocale } from '../../utils/locale';

const GoogleAdsDatePicker: React.FC = () => {
  const { t } = useTranslation();
  const theme = useTheme();
  const { datePreset, startDate, endDate, setDateFilter } = useGoogleAdsDate();
  const anchorRef = useRef<HTMLDivElement>(null);
  const [open, setOpen] = useState(false);
  const [tempStart, setTempStart] = useState(startDate);
  const [tempEnd, setTempEnd] = useState(endDate);

  const handleOpen = () => {
    setTempStart(startDate);
    setTempEnd(endDate);
    setOpen(true);
  };

  const handlePresetSelect = (key: string) => {
    const preset = DATE_PRESETS.find(p => p.key === key);
    if (!preset) return;
    const range = preset.getRange();
    if (range) {
      setDateFilter(key, range.start, range.end);
      setTempStart(range.start);
      setTempEnd(range.end);
    } else {
      setDateFilter(key, '', '');
      setTempStart('');
      setTempEnd('');
    }
    if (key !== 'custom') setOpen(false);
  };

  const handleCustomApply = () => {
    setDateFilter('custom', tempStart, tempEnd);
    setOpen(false);
  };

  const getPresetLabel = () => { const label = DATE_PRESETS.find(p => p.key === datePreset)?.label; return label ? t(label) : t('Özel'); };

  const formatDate = (d: string) => {
    const date = dayjs(d);
    // Short month name of the UI language ("Oca" / "Jan"); noon UTC keeps the month in any time zone
    const month = new Intl.DateTimeFormat(getLocale(), { month: 'short', timeZone: 'UTC' })
      .format(new Date(Date.UTC(2000, date.month(), 15, 12)));
    return `${date.date()} ${month} ${date.year()}`;
  };

  const getDateRangeLabel = () => {
    if (!startDate && !endDate) return t('Tüm zamanlar');
    return `${startDate ? formatDate(startDate) : '?'} – ${endDate ? formatDate(endDate) : '?'}`;
  };

  return (
    <>
      <Box
        ref={anchorRef}
        onClick={handleOpen}
        sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75,
          cursor: 'pointer', userSelect: 'none',
        }}
      >
        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 500, whiteSpace: 'nowrap', display: { xs: 'none', lg: 'block' } }}>
          {getPresetLabel()}
        </Typography>
        <Box sx={{
          display: 'flex', alignItems: 'center', gap: 0.5,
          border: `1px solid ${theme.palette.divider}`,
          borderRadius: 1, px: 1.25, height: 34,
          transition: 'background-color 150ms',
          '&:hover': { backgroundColor: theme.palette.action.hover },
        }}>
          <CalendarIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
          {/* Phones show the short preset name; the full range does not fit next to the account picker */}
          <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap', display: { xs: 'none', sm: 'block' } }}>
            {getDateRangeLabel()}
          </Typography>
          <Typography variant="caption" sx={{ fontWeight: 500, color: 'text.primary', whiteSpace: 'nowrap', display: { xs: 'block', sm: 'none' } }}>
            {getPresetLabel()}
          </Typography>
          <ArrowDropDownIcon sx={{ color: 'text.secondary', fontSize: 16 }} />
        </Box>
      </Box>

      <Popover
        open={open}
        anchorEl={anchorRef.current}
        onClose={() => setOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        slotProps={{ paper: { elevation: 8, sx: { mt: 0.5, overflow: 'hidden' } } }}
      >
        <Box sx={{ display: 'flex' }}>
          <Box sx={{ width: 220, borderRight: `1px solid ${theme.palette.divider}`, maxHeight: 440, overflowY: 'auto' }}>
            <List dense disablePadding>
              <ListItemButton
                selected={datePreset === 'custom'}
                onClick={() => setDateFilter('custom', tempStart, tempEnd)}
                sx={{ py: 1 }}
              >
                <ListItemText primary={t('Özel')} primaryTypographyProps={{ fontSize: '0.875rem' }} />
              </ListItemButton>
              <Divider />
              {DATE_PRESETS.map((p) => (
                <ListItemButton
                  key={p.key}
                  selected={datePreset === p.key}
                  onClick={() => handlePresetSelect(p.key)}
                  sx={{ py: 0.75 }}
                >
                  <ListItemText primary={t(p.label)} primaryTypographyProps={{ fontSize: '0.875rem', fontWeight: datePreset === p.key ? 600 : 400 }} />
                </ListItemButton>
              ))}
            </List>
          </Box>
          <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 1.5, minWidth: 280 }}>
            <Typography variant="subtitle2" color="text.secondary" sx={{ mb: 0.5 }}>{t('Tarih Aralığı')}</Typography>
            <TextField type="date" size="small" label={t('Başlangıç tarihi')} value={tempStart}
              onChange={(e) => setTempStart(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <TextField type="date" size="small" label={t('Bitiş tarihi')} value={tempEnd}
              onChange={(e) => setTempEnd(e.target.value)} InputLabelProps={{ shrink: true }} fullWidth />
            <Button variant="contained" size="small" onClick={handleCustomApply} sx={{ mt: 0.5 }}>{t('Uygula')}</Button>
          </Box>
        </Box>
      </Popover>
    </>
  );
};

export default GoogleAdsDatePicker;
